import React, { useMemo } from 'react';
import { BoxProps, Flex, JoyKitThemeProvider, TooltipV2, styled } from '@withjoy/joykit';
import { addAction } from '@shared/utils/logger';
import { useTranslation } from '@shared/core';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useEventInfo } from '@shared/utils/eventInfo';
import { GetEventCTAButtonsDataQueryVariables, useGetEventCTAButtonsDataQuery } from '@graphql/generated';
import { useFeatureValue } from '@shared/core/featureFlags';

const StatusLabel = styled(Flex)`
  z-index: 1;
  display: flex;
  align-items: center;
  border-radius: ${({ theme }) => theme.radii.full};
  background-color: rgba(247, 247, 247, 0.6);
  backdrop-filter: blur(4px);
  color: ${({ theme }) => theme.colors.mono14};
  font-weight: 500;
  font-family: Inter UI;
  letter-spacing: -0.032px;
  align-items: center;
  justify-content: center;
  padding: 0 ${({ theme }) => theme.space[5]};
  height: ${({ theme }) => theme.space[7]};
`;

const countForVariant = (variant: string, seed: string, currentTime?: Date) => {
  // Convert seed to a consistent number between 0 and 1
  const seedNumber = (parseInt(seed, 36) % 1000) / 1000;
  // simulate a time-based coefficient for peak hours (2 PM)
  const currentHour = (currentTime || new Date()).getHours();
  const timeCoefficient = Math.max(0, 1 - Math.abs(currentHour - 14) / 12);

  if (variant === 'singles') {
    return Math.floor(timeCoefficient * (seedNumber * 10) + 1);
  }
  if (variant === 'doubles') {
    return Math.floor(timeCoefficient * (seedNumber * 50) + 50);
  }
  if (variant === 'triples') {
    return Math.floor(timeCoefficient * (seedNumber * 500) + 500);
  }
  return undefined;
};

const LiveIndicator = (props: Omit<Props, 'seed'> & { count: number }) => {
  const { t } = useTranslation('guestSiteCustom');
  const { count, ...restProps } = props;
  return (
    <JoyKitThemeProvider>
      <TooltipV2
        content={
          <Flex flexDirection="column" gap={2}>
            <Flex typographyVariant={'label3'} color="white">
              {t('peopleCurrentlyViewingTooltipTitle')({ count })}
            </Flex>
            <Flex typographyVariant={'label2'} color="white">
              {t('peopleCurrentlyLookingTooltipBody')()}
            </Flex>
          </Flex>
        }
      >
        <StatusLabel gap={3} {...restProps}>
          {t('peopleCurrentlyViewing')({
            count
          })}
        </StatusLabel>
      </TooltipV2>
    </JoyKitThemeProvider>
  );
};

const FakeIndicator = (props: Props) => {
  const { seed, ...restProps } = props;
  const { value, loading } = useFeatureValue('accommodationsActiveViewingLabelExperiment');

  if (loading) {
    return null;
  }

  if (!value || ['singles', 'doubles', 'triples'].indexOf(value) === -1) {
    return null;
  }

  const count = countForVariant(value, seed);
  if (count === undefined) {
    addAction(`FakeData: count not valid for seed ${seed}`);
    return null;
  }
  return <LiveIndicator count={count} {...restProps} />;
};

type Props = { seed: string } & BoxProps;
export const LiveIndicatorExperiment = (props: { seed: string } & BoxProps) => {
  const { eventHandle } = useEventInfo();
  const { isAdmin } = useEventUserRole();

  const payloadGetEventCTAButtonsData: GetEventCTAButtonsDataQueryVariables | null = useMemo(() => {
    if (!eventHandle) {
      return null;
    }
    return {
      name: eventHandle
    };
  }, [eventHandle]);

  const { data } = useGetEventCTAButtonsDataQuery({
    variables: payloadGetEventCTAButtonsData!,
    fetchPolicy: 'cache-only',
    batchMode: 'fast',
    ssr: false,
    skip: !payloadGetEventCTAButtonsData
  });

  const rsvpEnabled = data?.eventByName?.rsvpPage?.disabled === false;
  // only show to non-admins and when rsvp is enabled
  if (isAdmin || !rsvpEnabled) {
    return null;
  }
  return <FakeIndicator {...props} />;
};
